import localCache from '@/utils/cache'

const SESSION_CACHE_KEYS = [
  'token',
  'userInfo',
  'userMenu',
  'depts',
  'empData',
  'companyData'
] as const

export interface SessionSnapshot {
  token: string
  userInfo: Record<string, any>
  userMenu: any[]
  depts: any[]
}

export class LoginSessionError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'LoginSessionError'
  }
}

const isNonEmptyRecord = (value: unknown): value is Record<string, any> => {
  return (
    Boolean(value) &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.keys(value as Record<string, unknown>).length > 0
  )
}

export function isCompleteSession(session: {
  token?: unknown
  userInfo?: unknown
  userMenu?: unknown
}): boolean {
  const userInfo = session.userInfo
  return (
    typeof session.token === 'string' &&
    session.token.trim().length > 0 &&
    isNonEmptyRecord(userInfo) &&
    Array.isArray(userInfo.roles) &&
    Array.isArray(session.userMenu) &&
    session.userMenu.length > 0
  )
}

export function readSessionCache(): SessionSnapshot | null {
  const session = {
    token: localCache.getCache('token'),
    userInfo: localCache.getCache('userInfo'),
    userMenu: localCache.getCache('userMenu'),
    depts: localCache.getCache('depts')
  }

  if (!isCompleteSession(session)) return null

  return {
    token: session.token,
    userInfo: session.userInfo,
    userMenu: session.userMenu,
    depts: Array.isArray(session.depts) ? session.depts : []
  }
}

export function hasCompleteSessionCache(): boolean {
  return readSessionCache() !== null
}

export function writeSessionCache(session: SessionSnapshot) {
  if (!isCompleteSession(session)) {
    throw new LoginSessionError('登录信息不完整，请重新登录')
  }

  try {
    localCache.setCache('token', session.token)
    localCache.setCache('userInfo', session.userInfo)
    localCache.setCache('userMenu', session.userMenu)
    localCache.setCache('depts', session.depts)
  } catch (error) {
    clearSessionCache()
    throw error
  }
}

export function setTemporarySessionToken(token: string) {
  if (!token.trim()) {
    throw new LoginSessionError('登录响应缺少 token，请稍后重试')
  }
  localCache.setCache('token', token)
}

export function clearSessionCache() {
  SESSION_CACHE_KEYS.forEach((key) => localCache.deleteKey(key))
}
